import type { EmailBrandingKind } from "services/types/email-branding.type";

import EnumDataSource from "../utils/enum-data-source.util";

export const MESSAGES_LIMIT = 30;

export const MESSAGE_TYPE_NOTIFICATION = 1;
export const MESSAGE_TYPE_REMINDER_REQUEST = 2;
export const MESSAGE_TYPE_REMINDER_RESPONSE = 3;
export const MESSAGE_TYPE_REMINDER_ACKNOWLEDGMENT = 4;
export const MESSAGE_TYPE_APPOINTMENT_INFORMATION = 5;
export const MESSAGE_TYPE_TEXTING = 6;
export const MESSAGE_TYPE_PRIVATE_FEEDBACK_REQUEST = 7;
export const MESSAGE_TYPE_PRIVATE_FEEDBACK_RESPONSE = 8;
export const MESSAGE_TYPE_PRIVATE_FEEDBACK_TEXTUAL_REQUEST = 9;
export const MESSAGE_TYPE_PRIVATE_FEEDBACK_TEXTUAL_RESPONSE = 10;
export const MESSAGE_TYPE_GOOGLE_REVIEW_FEEDBACK_REQUEST = 11;
export const MESSAGE_TYPE_RESCHEDULE = 12;
export const MESSAGE_TYPE_INTAKE_FORM_REQUEST = 13;
export const MESSAGE_TYPE_RESCHEDULE_REQUEST = 22;
export const MESSAGE_TYPE_NO_SHOW = 23;
export const MESSAGE_TYPE_APPOINTMENT_CANCELLED = 27;

/**
 * Message types whose send path carries an appointment, and therefore the only ones whose templates
 * can resolve the telehealth merge tags.
 *
 * Mirrors `TELEHEALTH_MESSAGE_TYPES` in
 * `suno_backend/communication/models/message_template.py` — the backend list that decides which
 * templates get the telehealth block. Keep the two in step: offering the tags anywhere else puts a
 * token in an editor that renders blank for every recipient, and because Unlayer writes the same
 * token into a link's href AND its visible text, the resulting `<a href=""></a>` is invisible
 * rather than obviously broken.
 */
export const TELEHEALTH_MESSAGE_TYPES: ReadonlySet<number> = new Set([
  MESSAGE_TYPE_NOTIFICATION,
  MESSAGE_TYPE_REMINDER_REQUEST,
  MESSAGE_TYPE_REMINDER_ACKNOWLEDGMENT,
  MESSAGE_TYPE_APPOINTMENT_INFORMATION,
  MESSAGE_TYPE_RESCHEDULE,
  MESSAGE_TYPE_APPOINTMENT_CANCELLED
]);
export const MESSAGE_TYPE_INTAKE_FORM_REQUEST_REMINDER = 14;
export const MESSAGE_TYPE_CAMPAIGN_PATIENT_BIRTHDAY = 15;
export const MESSAGE_TYPE_CAMPAIGN_HEARING_AID_WARRANTY_REPLACEMENT_EXPIRING = 16;
export const MESSAGE_TYPE_CAMPAIGN_HEARING_AID_WARRANTY_REPLACEMENT_EXPIRING_REMINDER = 17;
export const MESSAGE_TYPE_CAMPAIGN_HEARING_AID_PURCHASED_DAY_1 = 18;
export const MESSAGE_TYPE_MISSED_CALL = 37;
export const MESSAGE_TYPE_VOICEMAIL = 56;
export const MESSAGE_TYPE_CUSTOM_CAMPAIGN = 99;

const messageTypes = {
  [MESSAGE_TYPE_NOTIFICATION]: "Notification",
  [MESSAGE_TYPE_REMINDER_REQUEST]: "Reminder Request",
  [MESSAGE_TYPE_REMINDER_RESPONSE]: "Reminder Response",
  [MESSAGE_TYPE_REMINDER_ACKNOWLEDGMENT]: "Reminder Acknowledgment",
  [MESSAGE_TYPE_APPOINTMENT_INFORMATION]: "Appointment Information",
  [MESSAGE_TYPE_TEXTING]: "Texting",
  [MESSAGE_TYPE_PRIVATE_FEEDBACK_REQUEST]: "Private Feedback Request",
  [MESSAGE_TYPE_PRIVATE_FEEDBACK_RESPONSE]: "Private Feedback Response",
  [MESSAGE_TYPE_PRIVATE_FEEDBACK_TEXTUAL_REQUEST]:
    "Private Feedback Textual Request",
  [MESSAGE_TYPE_PRIVATE_FEEDBACK_TEXTUAL_RESPONSE]:
    "Private Feedback Textual Response",
  [MESSAGE_TYPE_GOOGLE_REVIEW_FEEDBACK_REQUEST]:
    "Google Review Feedback Request",
  [MESSAGE_TYPE_RESCHEDULE]: "Reschedule",
  [MESSAGE_TYPE_INTAKE_FORM_REQUEST]: "Intake Form Request",
  [MESSAGE_TYPE_INTAKE_FORM_REQUEST_REMINDER]: "Intake Form Request Reminder",
  [MESSAGE_TYPE_CAMPAIGN_PATIENT_BIRTHDAY]: "Campaign Patient Birthday",
  [MESSAGE_TYPE_VOICEMAIL]: "Voicemail",
  [MESSAGE_TYPE_CAMPAIGN_HEARING_AID_WARRANTY_REPLACEMENT_EXPIRING]:
    "Campaign Hearing Aid Warranty Replacement Expiring",
  [MESSAGE_TYPE_CAMPAIGN_HEARING_AID_WARRANTY_REPLACEMENT_EXPIRING_REMINDER]:
    "Campaign Hearing Aid Warranty Replacement Expiring Reminder",
  [MESSAGE_TYPE_CAMPAIGN_HEARING_AID_PURCHASED_DAY_1]:
    "Campaign Hearing Aid Purchased Day 1",
  [MESSAGE_TYPE_RESCHEDULE_REQUEST]: "Reschedule Request",
  [MESSAGE_TYPE_NO_SHOW]: "No Show",
  [MESSAGE_TYPE_APPOINTMENT_CANCELLED]: "Appointment Cancelled"
};

export const MESSAGE_TYPES = new EnumDataSource(messageTypes);

/**
 * Which email-branding kind (SN-19904) each email message type is branded as. Mirrors the backend
 * registry `EMAIL_BRANDING_KIND` (`communication` app), which is what the send actually reads; this
 * copy only drives the read-only notice in template editors. A type in no kind is branded as today.
 */
const EMAIL_BRANDING_MESSAGE_TYPES: Record<
  Exclude<EmailBrandingKind, "manual">,
  readonly number[]
> = {
  appointments: [1, 2, 4, 5, 12, 22, 23, 27],
  feedback: [7, 9, 11, 24],
  forms: [13, 14, 33],
  billing: [45, 46, 47, 48, 49, 50, 51, 52],
  marketing: [15, 16, 17, 18, 19, 20, 21, 32, 38, 40, 53, 54, 55, 98, 99],
  staff: [25, 26, 28, 29, 30, 31, 34, 35, 36, 43, 44, 57, 58, 59]
};

export const EMAIL_BRANDING_KIND_BY_MESSAGE_TYPE: ReadonlyMap<
  number,
  EmailBrandingKind
> = new Map(
  Object.entries(EMAIL_BRANDING_MESSAGE_TYPES).flatMap(([kind, types]) =>
    types.map((type) => [type, kind as EmailBrandingKind] as const)
  )
);

export const MESSAGE_DIRECTION_OUTBOUND = 1;
export const MESSAGE_DIRECTION_INBOUND = 2;

const messageDirections = {
  [MESSAGE_DIRECTION_OUTBOUND]: "Outbound",
  [MESSAGE_DIRECTION_INBOUND]: "Inbound"
};

export const MESSAGE_DIRECTIONS = new EnumDataSource(messageDirections);

export const FROM_STAFF = 1;

export const MESSAGE_STATUS_PENDING = 1;
export const MESSAGE_STATUS_SENT = 2;
export const MESSAGE_STATUS_EXPIRED = 3;
export const MESSAGE_STATUS_MUTED = 4;
export const MESSAGE_STATUS_SEND_ERROR = 5;
export const MESSAGE_STATUS_INVALID_NUMBER = 6;
export const MESSAGE_STATUS_QUEUED = 7;
export const MESSAGE_STATUS_ACCEPTED = 8;
export const MESSAGE_STATUS_DELIVERED = 9;
export const MESSAGE_STATUS_UNDELIVERED = 10;
export const MESSAGE_STATUS_FAILED = 11;
export const MESSAGE_STATUS_RECEIVED = 101;
export const MESSAGE_TYPE_COLLECTIONS_FRIENDLY_REMINDER = 45;
export const MESSAGE_TYPE_COLLECTIONS_FOLLOW_UP = 46;
export const MESSAGE_TYPE_COLLECTIONS_PAYMENT_PLAN_OFFER = 47;
export const MESSAGE_TYPE_COLLECTIONS_PAST_DUE_NOTICE = 48;
export const MESSAGE_TYPE_COLLECTIONS_FINAL_NOTICE = 49;
export const MESSAGE_TYPE_COLLECTIONS_PRE_COLLECTION_WARNING = 50;
export const MESSAGE_TYPE_PATIENT_BALANCE_PAYMENT_REQUEST_CREATED = 51;

const messageStatuses = {
  [MESSAGE_STATUS_PENDING]: "Pending",
  [MESSAGE_STATUS_SENT]: "Sent",
  [MESSAGE_STATUS_EXPIRED]: "Expired",
  [MESSAGE_STATUS_MUTED]: "Muted",
  [MESSAGE_STATUS_SEND_ERROR]: "Send Error",
  [MESSAGE_STATUS_INVALID_NUMBER]: "Invalid Number",
  [MESSAGE_STATUS_QUEUED]: "Queued",
  [MESSAGE_STATUS_ACCEPTED]: "Accepted",
  [MESSAGE_STATUS_DELIVERED]: "Delivered",
  [MESSAGE_STATUS_UNDELIVERED]: "Undelivered",
  [MESSAGE_STATUS_FAILED]: "Failed",
  [MESSAGE_STATUS_RECEIVED]: "Received"
};

export const MESSAGE_STATUSES = new EnumDataSource(messageStatuses);

/**
 * Outbound statuses a delivery re-check can still move. Mirrors
 * `RECONCILABLE_MESSAGE_STATUSES` in suno-backend — the endpoint rejects anything else with a 400,
 * so a status missing here hides the control and a status added here must be added there too.
 */
export const RECHECKABLE_MESSAGE_STATUSES: ReadonlySet<number> = new Set([
  MESSAGE_STATUS_SENT,
  MESSAGE_STATUS_QUEUED,
  MESSAGE_STATUS_ACCEPTED
]);

export const SUNO_COMMS_MESSAGE_TYPE_TEXT = 6;
export const SUNO_COMMS_MESSAGE_TYPE_EMAIL = 7;

export const SUNO_COMMS_MESSAGE_TYPES = new EnumDataSource({
  [SUNO_COMMS_MESSAGE_TYPE_TEXT]: "Text",
  [SUNO_COMMS_MESSAGE_TYPE_EMAIL]: "Email"
});
